import { AnyAction } from 'redux'
import { defaultHotelCities } from '@/components/hotels/defaultCities'
import {
  HotelAvailabilityFilter,
  HotelAvailabilityItem,
  HotelCity,
  HotelDetail,
  HotelFacility,
  HotelImage,
  HotelPriceBounds,
  HotelRoomView,
  HotelSearchValues,
  HotelTripBooking,
  HotelTripsListMeta,
} from '@/components/hotels/types'
import {
  FETCH_HOTEL_AVAILABILITY,
  FETCH_HOTEL_DETAILS,
  FETCH_HOTEL_PAGE,
  FETCH_HOTEL_ROOMS,
  FETCH_HOTEL_SEARCHED_CITIES,
  FETCH_HOTEL_TRIPS,
  SET_HOTEL_SEARCH,
} from '../actions/types'

export interface HotelState {
  cities: {
    data: HotelCity[]
    loading: boolean
  }
  searchedCities: {
    data: HotelCity[]
    loading: boolean
  }
  search: HotelSearchValues
  availability: {
    items: HotelAvailabilityItem[]
    filter: HotelAvailabilityFilter | null
    priceBounds: HotelPriceBounds | null
    loading: boolean
    error: string
    requestKey: string
  }
  hotelDetails: {
    byId: Record<number, HotelDetail>
    loading: boolean
    error: string
    requestKey: string
  }
  hotelPage: {
    hotelId: number | null
    detail: HotelDetail | null
    facilities: HotelFacility[]
    galleries: HotelImage[]
    rooms: HotelRoomView[]
    roomsLoading: boolean
    roomsError: string
    loading: boolean
    error: string
  }
  trips: {
    items: HotelTripBooking[]
    loading: boolean
    loadingMore: boolean
    meta: HotelTripsListMeta
  }
}

export const defaultHotelSearch: HotelSearchValues = {
  city: null,
  dateFrom: '',
  dateTo: '',
}

const initialState: HotelState = {
  cities: {
    data: defaultHotelCities,
    loading: false,
  },
  searchedCities: {
    data: [],
    loading: false,
  },
  search: defaultHotelSearch,
  availability: {
    items: [],
    filter: null,
    priceBounds: null,
    loading: false,
    error: '',
    requestKey: '',
  },
  hotelDetails: {
    byId: {},
    loading: false,
    error: '',
    requestKey: '',
  },
  hotelPage: {
    hotelId: null,
    detail: null,
    facilities: [],
    galleries: [],
    rooms: [],
    roomsLoading: false,
    roomsError: '',
    loading: false,
    error: '',
  },
  trips: {
    items: [],
    loading: false,
    loadingMore: false,
    meta: {
      current_page: 1,
      per_page: 10,
      total: 0,
    },
  },
}

export const hotelReducer = (
  state: HotelState = initialState,
  action: AnyAction,
): HotelState => {
  switch (action.type) {
    case FETCH_HOTEL_SEARCHED_CITIES:
      return {
        ...state,
        searchedCities: {
          data: action.data || [],
          loading: action.loading,
        },
      }
    case SET_HOTEL_SEARCH:
      return { ...state, search: action.values }
    case FETCH_HOTEL_AVAILABILITY:
      return {
        ...state,
        availability: {
          items:
            action.items !== undefined
              ? action.items
              : state.availability.items,
          filter:
            action.filter !== undefined
              ? action.filter
              : state.availability.filter,
          priceBounds:
            action.priceBounds !== undefined
              ? action.priceBounds
              : state.availability.priceBounds,
          loading: action.loading,
          error: action.error || '',
          requestKey: action.requestKey || state.availability.requestKey,
        },
      }
    case FETCH_HOTEL_PAGE:
      return {
        ...state,
        hotelPage: {
          hotelId:
            action.hotelId !== undefined
              ? action.hotelId
              : state.hotelPage.hotelId,
          detail:
            action.detail !== undefined
              ? action.detail
              : state.hotelPage.detail,
          facilities:
            action.facilities !== undefined
              ? action.facilities
              : state.hotelPage.facilities,
          galleries:
            action.galleries !== undefined
              ? action.galleries
              : state.hotelPage.galleries,
          rooms:
            action.rooms !== undefined ? action.rooms : state.hotelPage.rooms,
          roomsLoading: state.hotelPage.roomsLoading,
          roomsError:
            action.roomsError !== undefined
              ? action.roomsError
              : state.hotelPage.roomsError,
          loading: action.loading,
          error: action.error || '',
        },
      }
    case FETCH_HOTEL_ROOMS:
      return {
        ...state,
        hotelPage: {
          ...state.hotelPage,
          rooms:
            action.rooms !== undefined ? action.rooms : state.hotelPage.rooms,
          roomsLoading: action.loading,
          roomsError: action.error || '',
        },
      }
    case FETCH_HOTEL_DETAILS:
      return {
        ...state,
        hotelDetails: {
          byId: action.byId
            ? { ...state.hotelDetails.byId, ...action.byId }
            : state.hotelDetails.byId,
          loading: action.loading,
          error: action.error || '',
          requestKey: action.requestKey || state.hotelDetails.requestKey,
        },
      }
    case FETCH_HOTEL_TRIPS:
      return {
        ...state,
        trips: {
          items: action.items !== undefined ? action.items : state.trips.items,
          loading: action.loading ?? state.trips.loading,
          loadingMore: action.loadingMore ?? false,
          meta: action.meta || state.trips.meta,
        },
      }
    default:
      return state
  }
}
